/**
 * Typed error thrown by the API client for any non-2xx response. Wraps the
 * server error envelope plus the HTTP status so UI code can branch on either
 * the stable `code` (preferred) or the `httpStatus` for transport-level cases.
 */
export class AuthApiError extends Error {
    code;
    httpStatus;
    constructor(envelope, httpStatus) {
        super(envelope.message);
        this.name = 'AuthApiError';
        this.code = envelope.errorCode;
        this.httpStatus = httpStatus;
    }
    /**
     * Returns true if the error is one of the well-known field-level codes the
     * UI typically wants to surface near a specific input rather than as a banner.
     */
    isFieldError() {
        return (this.code === 'INVALID_CREDENTIALS' ||
            this.code === 'IDENTITY_ALREADY_EXISTS' ||
            this.code === 'WEAK_PASSWORD' ||
            this.code === 'INVALID_CODE' ||
            this.code === 'IDENTITY_NOT_FOUND' ||
            this.code === 'INVALID_GROUP');
    }
}
/**
 * Catch-all UI string lookup keyed by `errorCode`. Covers the codes documented
 * in the API contract; anything missing falls back to the server-supplied
 * message. Host apps can spread this with their own overrides.
 */
export const defaultErrorMessages = {
    INVALID_CREDENTIALS: 'Email or password is incorrect.',
    IDENTITY_ALREADY_EXISTS: 'An account with that email already exists.',
    WEAK_PASSWORD: 'That password is too weak. Use at least 8 characters with a mix of letters and numbers.',
    FLOW_EXPIRED: 'This step expired. Start over to receive a new code.',
    INVALID_CODE: 'That code is not correct. Check the code and try again.',
    IDENTITY_NOT_FOUND: 'No account matches that email.',
    REQUIRES_MFA: 'Additional verification is required to finish signing in.',
    INVALID_GROUP: 'One of the selected groups is not allowed for this app.',
    PERMISSION_DENIED: 'You do not have permission to perform that action.',
    AUTHENTICATION_REQUIRED: 'Sign in to continue.',
};
/**
 * Resolves a user-facing message for an error. Prefers the lookup override,
 * then `defaultErrorMessages`, then the server message; falls back to a generic
 * sentence when nothing else is available.
 */
export function resolveErrorMessage(error, overrides = {}) {
    if (error instanceof AuthApiError) {
        return overrides[error.code] ?? defaultErrorMessages[error.code] ?? error.message;
    }
    if (error instanceof Error && error.message.length > 0)
        return error.message;
    return 'Something went wrong. Please try again.';
}
//# sourceMappingURL=errors.js.map